import React, { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { useAlertsCount } from '../../features/ativos/hooks';
import { formatBrasilia } from '../lib/date';

const TYPE_LABELS: Record<string, string> = {
  deadline_warning: 'Prazo de avaliação',
  // SPEC 02 Fase E — alerta gerado por ClientDocumentsCronService.
  'client.document.expiring': 'Documento do cliente próximo do vencimento',
  // SPEC 03 Fase D — eventos de contato (US-COM-003).
  'contact.created': 'Novo contato do cliente',
  'contact.updated': 'Contato do cliente atualizado',
  'contact.deleted': 'Contato do cliente removido',
  'contact.set_principal': 'Novo contato principal',
  'contact.deactivated': 'Contato do cliente inativado',
  'contact.substituted': 'Contato substituído',
  'contact.absence_started': 'Afastamento de contato iniciado',
  'contact.absence_ended': 'Afastamento de contato encerrado',
  'contact.without_valid_channel': 'Contato sem canal de comunicação',
  'contact.stale': 'Contato sem atualização há mais de 1 ano',
  'client.missing_required_contact': 'Cliente sem contato obrigatório',
  // US-GP-002 — documentos do colaborador.
  'people.document.rejected': 'Documento rejeitado',
  'people.document.correction_requested': 'Correção de documento solicitada',
  'people.document.expiring': 'Documento próximo do vencimento',
  'people.document.expired': 'Documento vencido',
  // US-GP-011/012 — férias.
  vacation_alert: 'Alerta de férias',
  vacation_request_pending: 'Férias aguardando aprovação',
  vacation_request_decided: 'Decisão sobre férias',
  // US-GP-015 — férias para o gestor do cliente.
  vacation_client_update: 'Férias da sua equipe',
  vacation_client_conflict: 'Conflito informado pelo cliente',
  // US-COM-005 — ciclo da Base Comercial.
  'commercial_base.approval_requested': 'Base Comercial aguardando aprovação',
  'commercial_base.approved': 'Base Comercial aprovada',
  'commercial_base.rejected': 'Base Comercial rejeitada',
  'commercial_base.published': 'Base Comercial publicada',
  'commercial_base.expiring_soon': 'Base Comercial próxima do vencimento',
  'commercial_base.expired': 'Base Comercial vencida',
  'commercial_base.adjustment_applied': 'Reajuste aplicado à Base Comercial',
  'commercial_base.adjustment_pending': 'Reajuste pendente na Base Comercial',
  'commercial_base.discount_approval_needed': 'Desconto aguardando alçada',
  'commercial_base.approval_stale': 'Aprovação de Base Comercial parada',
  'cau.event': 'Central de Atendimento',
};

/**
 * Renderiza a linha detalhe do payload de acordo com o `type` da
 * notificação. Extrai o payload em variáveis tipadas leves para não
 * poluir o JSX principal.
 */
const renderPayloadDetail = (type: string, payload: unknown): React.ReactNode => {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const client = p.clientCode ? String(p.clientCode) : null;
  const contactNome = p.contactNome ? String(p.contactNome) : null;
  const newContactNome = p.newContactNome ? String(p.newContactNome) : null;
  const substituteContactNome = p.substituteContactNome ? String(p.substituteContactNome) : null;
  const endsAt = p.endsAt ? String(p.endsAt) : null;
  const missingLabel = p.missingResponsibilityLabel ? String(p.missingResponsibilityLabel) : null;

  switch (type) {
    case 'cau.event':
      return <>{p.title && <span className="font-medium">{String(p.title)}</span>}{p.message && <span> — {String(p.message)}</span>}</>;
    case 'contact.created':
    case 'contact.updated':
    case 'contact.deleted':
    case 'contact.set_principal':
    case 'contact.deactivated':
    case 'contact.without_valid_channel':
    case 'contact.stale':
      return (
        <>
          {client && <span className="font-medium">{client}</span>}
          {client && contactNome && <span> — </span>}
          {contactNome}
        </>
      );
    case 'contact.substituted':
      return (
        <>
          {client && <span className="font-medium">{client}</span>}
          {client && <span> — </span>}
          {contactNome} → <span className="font-medium">{newContactNome}</span>
        </>
      );
    case 'contact.absence_started':
      return (
        <>
          {client && <span className="font-medium">{client}</span>}
          {client && <span> — </span>}
          {contactNome} (substituto: {substituteContactNome}){endsAt && `, retorna ${endsAt}`}
        </>
      );
    case 'contact.absence_ended':
      return (
        <>
          {client && <span className="font-medium">{client}</span>}
          {client && <span> — </span>}
          {contactNome} retornou
        </>
      );
    case 'client.missing_required_contact':
      return (
        <>
          {client && <span className="font-medium">{client}</span>}
          {client && <span> — </span>}
          faltando <span className="font-medium">{missingLabel}</span>
        </>
      );
    case 'commercial_base.approval_requested':
    case 'commercial_base.approved':
    case 'commercial_base.rejected':
    case 'commercial_base.published':
    case 'commercial_base.expiring_soon':
    case 'commercial_base.expired':
    case 'commercial_base.adjustment_applied':
    case 'commercial_base.adjustment_pending':
    case 'commercial_base.discount_approval_needed':
    case 'commercial_base.approval_stale': {
      const codigo = p.codigo ? String(p.codigo) : null;
      const nome = p.nome ? String(p.nome) : null;
      const versao = p.versao != null ? String(p.versao) : null;
      const justificativa = p.justificativa ? String(p.justificativa) : null;
      const diasRestantes = p.diasRestantes != null ? String(p.diasRestantes) : null;
      const diasParado = p.diasParado != null ? String(p.diasParado) : null;
      const percentual = p.percentual ? String(p.percentual) : null;
      const percentualSolicitado = p.percentualSolicitado ? String(p.percentualSolicitado) : null;
      const percentualLimite = p.percentualLimite ? String(p.percentualLimite) : null;
      const competencia = p.competencia ? String(p.competencia) : null;

      // Detalhe extra depende do tipo — o cabeçalho comum é sempre `código v.N — nome`.
      let extra: React.ReactNode = null;
      if (type === 'commercial_base.rejected' && justificativa) {
        extra = <> — <em>{justificativa}</em></>;
      } else if (type === 'commercial_base.expiring_soon' && diasRestantes) {
        extra = <> — vence em {diasRestantes} dia{diasRestantes === '1' ? '' : 's'}</>;
      } else if (type === 'commercial_base.adjustment_applied' && percentual) {
        extra = <> — {percentual}%{competencia ? ` (${competencia})` : ''}</>;
      } else if (type === 'commercial_base.adjustment_pending' && competencia) {
        extra = <> — competência {competencia}</>;
      } else if (
        type === 'commercial_base.discount_approval_needed' &&
        percentualSolicitado &&
        percentualLimite
      ) {
        extra = <> — {percentualSolicitado}% (limite {percentualLimite}%)</>;
      } else if (type === 'commercial_base.approval_stale' && diasParado) {
        extra = <> — parado há {diasParado} dia{diasParado === '1' ? '' : 's'}</>;
      }

      return (
        <>
          {codigo && <span className="font-medium">{codigo}</span>}
          {codigo && versao && <span> v.{versao}</span>}
          {codigo && nome && <span> — </span>}
          {nome}
          {extra}
        </>
      );
    }
    case 'people.document.rejected':
    case 'people.document.correction_requested':
    case 'people.document.expiring':
    case 'people.document.expired': {
      const nome = p.documentName ? String(p.documentName) : 'Documento';
      const detalhe = p.comment ? String(p.comment) : p.expiresAt ? `validade ${String(p.expiresAt).split('-').reverse().join('/')}` : null;
      return (
        <span>
          {nome}
          {p.reason ? ` — ${String(p.reason)}` : ''}
          {detalhe ? `: ${detalhe}` : ''}
        </span>
      );
    }
    case 'vacation_alert': {
      const quem = p.personName ? String(p.personName) : null;
      return <span>{p.label ? String(p.label) : 'Férias'}{quem ? ` — ${quem}` : ''}</span>;
    }
    case 'vacation_client_update': {
      const k = { aprovada: 'aprovadas', cancelada: 'canceladas', reprogramada: 'reprogramadas' }[String(p.kind)] ?? 'atualizadas';
      return <span>{p.personName ? String(p.personName) : 'Profissional'} — férias {k}{p.startDate ? ` (início ${String(p.startDate).split('-').reverse().join('/')})` : ''}</span>;
    }
    case 'vacation_client_conflict':
      return <span>{p.clientName ? `${String(p.clientName)}: ` : ''}{p.personName ? String(p.personName) : ''}{p.justification ? ` — ${String(p.justification)}` : ''}</span>;
    case 'vacation_request_pending':
      return <span>{p.personName ? String(p.personName) : 'Colaborador'} — etapa {p.step === 'gestor' ? 'do gestor' : 'do RH'}</span>;
    case 'vacation_request_decided': {
      const decisao = { aprovado: 'Aprovadas', rejeitado: 'Reprovadas', devolvido: 'Devolvidas para ajuste' }[String(p.decision)] ?? 'Atualizadas';
      return <span>{decisao}{p.comment ? `: ${String(p.comment)}` : ''}</span>;
    }
    default:
      return null;
  }
};

/**
 * SPEC 04 — badge soma notificações pessoais + alertas do módulo Ativos
 * (open + acknowledged). Dropdown ganha seção "Alertas do módulo Ativos"
 * com link para a central.
 */
export const NotificationBell: React.FC = () => {
  const { notifications, unreadCount, markRead } = useNotifications();
  const alertsCount = useAlertsCount();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClick = async (id: string) => {
    await markRead(id);
  };

  const openAlerts = alertsCount.data?.open ?? 0;
  const ackedAlerts = alertsCount.data?.acknowledged ?? 0;
  const totalBadge = unreadCount + openAlerts;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
        aria-label="Notificações e alertas"
      >
        <Bell className="w-5 h-5" />
        {totalBadge > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full">
            {totalBadge > 9 ? '9+' : totalBadge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">
            Notificações {unreadCount > 0 && <span className="text-xs font-normal text-gray-400">({unreadCount} não lidas)</span>}
          </div>
          {notifications.length === 0 ? (
            <div className="px-4 py-4 text-center text-xs text-gray-400">Nenhuma notificação.</div>
          ) : (
            <ul className="max-h-56 overflow-y-auto divide-y divide-gray-50">
              {notifications.map(n => (
                <li
                  key={n.id}
                  onClick={() => handleClick(n.id)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                >
                  <p className="text-sm font-medium text-gray-800">{TYPE_LABELS[n.type] ?? n.type}</p>
                  {n.payload && (n.payload as any).periodName && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Ciclo: {(n.payload as any).periodName} — encerra em {(n.payload as any).endsAt}
                    </p>
                  )}
                  {n.type === 'client.document.expiring' && n.payload && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {(n.payload as any).clientCode} — {(n.payload as any).tipo}{' '}
                      <span className="font-medium">
                        (vence em {(n.payload as any).daysUntilExpiration} dia
                        {(n.payload as any).daysUntilExpiration === 1 ? '' : 's'})
                      </span>
                    </p>
                  )}
                  {/* SPEC 03 Fase D — payload rico dos 10 novos tipos de contato. */}
                  {n.payload && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {renderPayloadDetail(n.type, n.payload)}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">{formatBrasilia(n.createdAt)}</p>
                </li>
              ))}
            </ul>
          )}

          {/* SPEC 04 — Alertas do módulo Ativos */}
          <div className="border-t border-gray-100 bg-gray-50 px-4 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Alertas do módulo Ativos
            </p>
            <div className="flex items-center justify-between text-sm">
              <div>
                <span className={openAlerts > 0 ? 'font-semibold text-red-700' : 'text-gray-600'}>
                  {openAlerts} aberto{openAlerts === 1 ? '' : 's'}
                </span>
                {ackedAlerts > 0 && (
                  <span className="ml-2 text-xs text-gray-500">
                    · {ackedAlerts} reconhecido{ackedAlerts === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <Link
                to="/ativos/alertas"
                onClick={() => setOpen(false)}
                className="text-xs text-blue-600 hover:underline"
              >
                Ver central →
              </Link>
            </div>
          </div>
          <div className="border-t border-gray-100 px-4 py-2 text-right">
            <Link to="/cau/notificacoes" onClick={() => setOpen(false)} className="text-xs text-blue-600 hover:underline">Central do CAU →</Link>
          </div>
        </div>
      )}
    </div>
  );
};
